import { itemsAPI } from './itemsAPI';
import { designerItemsAPI } from './designerItemsAPI';
import { boutiqueItemsAPI } from './boutiqueItemsAPI';
import { weddingEssentialsAPI } from './weddingEssentialsAPI';
import { getListingDetailPath, getListingLinkState } from '../utils/itemLinks';

const PER_SOURCE = 24;

function pickImage(item) {
  return (
    item?.primary_image
    || item?.image_url
    || item?.images?.[0]?.image
    || item?.images?.[0]
    || null
  );
}

function normalizeRow(item, listingKind, sourceLabelKey) {
  const title =
    item.title
    || `${item.brand_name || ''} ${item.item_name || ''}`.trim()
    || 'Item';
  return {
    key: `${listingKind}-${item.id}`,
    id: item.id,
    title,
    price: item.price,
    image: pickImage(item),
    listingKind,
    sourceLabelKey,
    item_code: item.item_code,
    category: item.category,
    condition: item.condition || null,
    brand_name: item.brand_name || null,
    custom_brand_name: item.custom_brand_name || null,
    is_no_brand: item.is_no_brand ?? null,
    href: getListingDetailPath(item, listingKind),
    linkState: getListingLinkState(item, listingKind),
    raw: item,
  };
}

function rowsFromList(payload, listingKind, sourceLabelKey) {
  const list = Array.isArray(payload)
    ? payload
    : payload?.results || [];
  return list.map((row) =>
    normalizeRow(row, row.listingKind || listingKind, sourceLabelKey),
  );
}

/**
 * Search across marketplace (pre-owned / luxury / defected), designer,
 * boutique, and wedding listing sources in parallel.
 * Optional `category` narrows marketplace item queries and client-filters others.
 */
export async function searchAllListingTypes(query, { perSource = PER_SOURCE, category = '' } = {}) {
  const q = String(query || '').trim();
  const categoryFilter = String(category || '').trim();
  if (!q && !categoryFilter) return [];

  const filters = {};
  if (q) filters.search = q;
  if (categoryFilter) filters.category = categoryFilter;

  const settled = await Promise.allSettled([
    itemsAPI.getItems({ ...filters, page: 1, page_size: perSource }),
    itemsAPI.getItems({ ...filters, is_luxury: true, page: 1, page_size: perSource }),
    designerItemsAPI.getDesignerItemsPage(q ? { search: q } : {}, 1, perSource),
    boutiqueItemsAPI.getBoutiqueItemsPage(q ? { search: q } : {}, 1, perSource),
    weddingEssentialsAPI.getWeddingListingsPage(q ? { search: q } : {}, 1, perSource),
  ]);

  const [
    preOwned,
    luxury,
    designer,
    boutique,
    wedding,
  ] = settled;

  const merged = [];
  const seen = new Set();

  const matchesCategory = (row) => {
    if (!categoryFilter) return true;
    const itemCat = String(row.category || row.raw?.category || '').trim().toLowerCase();
    return itemCat === categoryFilter.toLowerCase();
  };

  const pushAll = (result, listingKind, sourceLabelKey) => {
    if (result.status !== 'fulfilled') return;
    for (const row of rowsFromList(result.value, listingKind, sourceLabelKey)) {
      if (seen.has(row.key)) continue;
      if (!matchesCategory(row)) continue;
      seen.add(row.key);
      merged.push(row);
    }
  };

  pushAll(preOwned, 'item', 'search.sourcePreOwned');
  pushAll(luxury, 'item', 'search.sourceLuxury');
  pushAll(designer, 'designer_item', 'search.sourceDesigner');
  pushAll(boutique, 'boutique_item', 'search.sourceBoutique');
  pushAll(wedding, 'item', 'search.sourceWedding');

  return merged;
}

const globalSearchAPI = { searchAllListingTypes };

export default globalSearchAPI;
